<template>
  <div class="game-wrapper game">
    <div class="game-inner">
      <v-card-text class="mb-4 pt-md-0 pt-sm-6 card-header">
        <div
          class="ms-0 align-start"
        >
          <v-row class="px-1 py-1">
            <v-col
              cols="12"
              class="py-1 px-3 d-flex justify-space-between align-center"
              :class="{'mt-3': isIOSComputed }"
            >
              <div class="d-flex">
                <router-link
                  to="/"
                  class="d-flex align-center text-decoration-none"
                >
                  <v-img
                    :src="appLogoTitle"
                    alt="logo"
                    width="auto"
                    max-width="125px"
                    max-height="30px"
                    contain
                    class="me-3"
                  />
                </router-link>
              </div>

              <div>
                <v-btn
                  v-if="!privateLanding"
                  class="ma-0 py-2 px-0 no-bg-hover font-weight-bold text-body-1"
                  style="
                    text-transform: inherit;
                    min-width: 60px;
                  "
                  text
                  color="primary"
                  @click="onLogin(false)"
                >
                  {{ $t('login.login') }}
                </v-btn>

                <app-bar-user-avatar
                  v-else
                  class="ms-2"
                  @logout="onLogout"
                />
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>

      <v-card-text
        v-if="cancelledComputed"
        class="pt-0"
      >
        <action-card
          :title="$t('game.match_cancelled')"
          :subtitle="$t('game.match_has_been_cancelled')"
        >
          <template #icon="{ on, attrs }">
            <v-icon
              color="#ff3232"
              class="ma-0"
              size="25"
              v-bind="attrs"
              v-on="on"
            >
              {{ icons.mdiCloseCircleOutline }}
            </v-icon>
          </template>
        </action-card>
      </v-card-text>

      <v-card-text class="pt-0">
        <info-card
          :title="dataComputed.sport_name"
          :subtitle="dataComputed.match_date_str"
          :date="`${formatStdDate(dataComputed.match_date_str, 'datetime-landing', true, $i18n.locale)} ${formatStdDate(dataComputed.start_time, 'time-short', true, $i18n.locale)} - ${formatStdDate(dataComputed.end_time, 'time-short', true, $i18n.locale)}`"
          :location="dataComputed.facility_name"
          :level="dataComputed.match_level_str === 'all' ? $t('game.any_level') : dataComputed.match_level_str"
          :price="dataComputed && dataComputed.total > 0 ? `${formatCurrency(dataComputed.total)}` : $t('status.free')"
          :gender-category="dataComputed.match_gender_str"
          :start-time="dataComputed.start_time_formatted ? `${dataComputed.start_time_formatted} (${formatTime(dataComputed.reservation_time)})` : ''"
          :cancel-date="dataComputed.refund_cancel_deadline? formatStdDate(dataComputed.refund_cancel_deadline, 'datetime', true, $i18n.locale) : null"
          :is-cancel="dataComputed.reservation_status === 'cancelled'"
          :can-cancel="dataComputed.can_cancel"
          :is-confirm="!!dataComputed.deposit_card"
          :confirm-code="depositCardComputed"
          :confirm-date="dataComputed.cancel_reservation_date_str"
          :is-paid="dataComputed.is_paid === 'Y'"
          :is-now="dataComputed.charge_reservation_date_str === 'now'"
          :payment-status="dataComputed.reservation_status === 'cancelled' ? dataComputed.reservation_status : dataComputed.payment_status"
          :disabled="cancelledComputed"
        />
      </v-card-text>

      <v-card-text
        v-if="dataComputed && dataComputed.match_status === 'P'"
        class="pt-0"
      >
        <results-card
          :my-team-name="dataComputed.my_team === null || dataComputed.my_team === 'A' ? getTeamName(dataComputed.teams || [], 'A', dataComputed.my_team, dataComputed.is_challenge === 'Y') : getTeamName(dataComputed.teams || [],'B', dataComputed.my_team, dataComputed.is_challenge === 'Y')"
          :rival-team-name="dataComputed.my_team === null || dataComputed.my_team === 'A' ? getTeamName(dataComputed.teams || [], 'B', dataComputed.my_team, dataComputed.is_challenge === 'Y') : getTeamName(dataComputed.teams || [],'A', dataComputed.my_team, dataComputed.is_challenge === 'Y')"
          :my-team-result="dataComputed.my_team === null || dataComputed.my_team === 'A' ? dataComputed.match_result_A : dataComputed.match_result_B"
          :rival-team-result="dataComputed.my_team === null || dataComputed.my_team === 'A' ? dataComputed.match_result_B : dataComputed.match_result_A"
          :my-team-shield="dataComputed.my_team === null || dataComputed.my_team === 'A' ? getTeamShield(dataComputed.teams || [], 'A', dataComputed.my_team, dataComputed.is_challenge === 'Y') : getTeamShield(dataComputed.teams || [],'B', dataComputed.my_team, dataComputed.is_challenge === 'Y')"
          :rival-team-shield="dataComputed.my_team === null || dataComputed.my_team === 'A' ? getTeamShield(dataComputed.teams || [], 'B', dataComputed.my_team, dataComputed.is_challenge === 'Y') : getTeamShield(dataComputed.teams || [],'A', dataComputed.my_team, dataComputed.is_challenge === 'Y')"
        />
      </v-card-text>

      <v-card-text
        v-if="!!userLogged && dataComputed && (dataComputed.can_rank_players)"
        class="pt-0"
      >
        <action-card
          :title="$t('landing.rate_players')"
          :subtitle="$t('landing.rate_players_msg')"
          avatar-gradient="background-image: linear-gradient(to bottom, #ff3232, #e22827, #c61e1c, #ab1412, #900808);"
          icon-color="#ffffff"
          :show-border="!!userLogged"
          border-color="#ff323240"
          :show-arrow="!!userLogged"
          @save="!!userLogged && dataComputed.is_joined ? linkVideo(false) : null"
        >
          <template #icon="{ attrs }">
            <img
              :src="icons.ranking"
              height="20px"
              width="20px"
              alt="video"
              attrs="video"
              style="background-position: center center; margin-top: 4px"
              v-bind="attrs"
            />
          </template>
        </action-card>
      </v-card-text>

      <v-card-text
        v-if="!!userLogged && dataComputed && dataComputed.can_add_result && !cancelledComputed"
        class="pt-0"
      >
        <action-card
          :title="$t('game.enter_results')"
          :subtitle="dataComputed.can_rank_players ? $t('game.enter_results_rank_msg') : $t('game.enter_results_msg')"
          avatar-gradient="background-image: linear-gradient(to bottom, #ff3232, #e22827, #c61e1c, #ab1412, #900808);"
          icon-color="#ffffff"
          :show-border="!!userLogged"
          border-color="#ff323240"
          :show-arrow="!!userLogged"
          :icon-container-styles="'width: 90%; height: auto;'"
          @save="!!userLogged && dataComputed.is_joined ? linkVideo(false) : null"
        >
          <template #icon="{ attrs }">
            <img
              :src="icons.frsWhite"
              height="20px"
              width="20px"
              alt="video"
              attrs="video"
              style="background-position: center center; margin-top: 4px"
              v-bind="attrs"
            />
          </template>
        </action-card>
      </v-card-text>

      <!-- TODO deek to video_detail -->
      <v-card-text
        v-if="dataComputed && dataComputed.video_id"
        class="pt-0"
      >
        <video-card
          :images="dataComputed.video_thumbnail ? [dataComputed.video_thumbnail] : []"
          :show-border="dataComputed.match_result_A === '?' && dataComputed.match_result_B === '?'"
          @open="linkVideo(true)"
        />
      </v-card-text>

      <v-card-text
        v-if="!cancelledComputed"
        class="pt-0"
      >
        <!-- // remove team -->
        <!-- is-challenge, my-team -->
        <!-- :show-edit="dataComputed.can_edit_players && false" -->
        <player-list
          :selected.sync="slotSelected"
          :team-selected="slotTeamSelected"
          :teams="teamsComputed"
          :player-quantity="dataComputed.match_players_qty"
          :show-actions="true"
          :show-edit="dataComputed.is_organizer === 'Y'"
          :is-private="privateLanding"
          :is-organizer="dataComputed.is_organizer === 'Y'"
          :is-player="dataComputed.is_player"
          :is-joined="dataComputed.is_joined"
          :is-challenge="dataComputed.is_challenge === 'Y'"
          :my-team="dataComputed.my_team"
          :route-team="teamSelected"
          :total-votes="dataComputed.total_votes || 0"
          :disabled="computedDisabled"
          :can-select-team="dataComputed.is_challenge === 'Y' && dataComputed.teams.length === 1"
          :cancelled="cancelledComputed"
          @refresh-match="fetchMatch"
          @download-app="openUpdateDownload"
          @open-game="linkVideo"
          @update-selected="(val) => slotSelected = val"
          @update-team-selected="(val) => slotTeamSelected = val"
          @edit-players="linkVideo"
          @share="openUpdateShare(true)"
        />
      </v-card-text>

      <v-card-text v-if="false">
        <action-card
          :title="'Chat del partido'"
          :subtitle="'Todos los jugadores participan del chat'"
          :show-arrow="true"
        >
          <template #icon="{ attrs }">
            <v-icon
              color="white"
              class="ma-0"
              size="25"
              v-bind="attrs"
            >
              {{ icons.mdiMessageText }}
            </v-icon>
          </template>
        </action-card>
      </v-card-text>

      <v-card-text
        v-if="!cancelledComputed"
        class="pt-0"
      >
        <action-card
          :title="dataComputed.match_type === 'competitive' ? $t('game.official_match') : $t('game.friendly_match')"
          :subtitle="dataComputed.match_type === 'competitive' ? $t('game.official_match_msg') : $t('game.friendly_match_msg')"
          :avatar-color="dataComputed.match_type === 'competitive' ? '#14AE5C' : '#F2F2F2'"
          :icon-container-styles="dataComputed.match_type === 'competitive' ? 'width: 90%; height: auto;' : 'display: flex;'"
          :cancelled="cancelledComputed"
        >
          <template #icon="{ attrs }">
            <img
              :src="dataComputed.match_type === 'competitive' ? icons.frsWhite : icons.smileyHappy"
              height="20px"
              width="20px"
              alt="level"
              attrs="level"
              style="background-position: center center"
              v-bind="attrs"
            />
          </template>
        </action-card>
      </v-card-text>

      <v-card-text
        class="pt-0"
      >
        <action-card
          :title="dataComputed.include_video === 'Y' ? $t('game.video_recorded') : $t('game.not_recorded')"
          :subtitle="dataComputed.include_video === 'Y' ? $t('game.video_recorded_msg'): $t('game.not_recorded_msg')"
          :avatar-color="dataComputed.include_video === 'Y' ? '#14AE5C' : '#F2F2F2'"
          icon-color="#14AE5C"
          :cancelled="cancelledComputed"
        >
          <template #icon="{ attrs }">
            <img
              :src="dataComputed.include_video === 'Y' ? icons.videoWhite : icons.videoGray"
              height="20px"
              width="20px"
              alt="video"
              attrs="video"
              style="background-position: center center; margin-top: 4px"
              v-bind="attrs"
            />
          </template>
        </action-card>
      </v-card-text>

      <v-card-text
        :style="((dataComputed.can_leave && !cancelledComputed) || dataComputed.can_cancel) ? '' : 'padding-bottom: 140px;'"
        class="pt-0"
      >
        <facility-card
          :facility-name="dataComputed.facility_name"
          :facility-address="dataComputed.facility_short_address"
          :field-name="dataComputed.field_name"
          :images="dataComputed.facility_images"
          :latitude="dataComputed.facility_latitude"
          :longitude="dataComputed.facility_longitude"
          :disabled="cancelledComputed"
        />
      </v-card-text>

      <v-card-text
        v-if="(dataComputed.can_leave && !cancelledComputed) || dataComputed.can_cancel"
        style="padding-bottom: 140px;"
        class="text-center"
      >
        <v-btn
          v-if="dataComputed.can_leave && !cancelledComputed"
          class="mt-1 py-2 no-bg-hover"
          color="primary"
          style="text-transform: none; font-weight: 700; padding-left: 0px; text-decoration: underline;"
          text
          @click="openUpdateLeave(true)"
        >
          <span>{{ $t('game.unjoin') }}</span>
        </v-btn>

        <v-btn
          v-if="dataComputed.can_cancel"
          class="mt-1 py-2 no-bg-hover"
          color="primary"
          style="text-transform: none; font-weight: 700; padding-left: 0px; text-decoration: underline;"
          text
          @click="linkVideo"
        >
          <span>{{ $t('tooltip.cancel') }}</span>
        </v-btn>
      </v-card-text>

      <v-card-actions
        v-if="dataComputed.match_status !== 'C'"
        class="pay-card-action py-3 d-flex- flex-column"
      >
        <v-btn
          v-if="(dataComputed.is_challenge === 'Y' && dataComputed.teams.length === 1 && slotTeamSelected !== -1 && dataComputed.match_status !== 'C')"
          class="text-body-1 font-weight-bold mb-2"
          style="border-radius: 33px; text-transform: initial;"
          color="primary"
          rounded
          large
          block
          @click="onChallengeTeam"
        >
          <span>{{ $t('game.challenge_team') }}</span>
        </v-btn>

        <v-btn
          v-if="(
            (dataComputed.is_challenge === 'N' && dataComputed.can_join) ||
            (
              dataComputed.is_challenge === 'Y' && slotTeamSelected === -1 &&
              (dataComputed.can_join || (!dataComputed.can_join && (dataComputed.can_join_free_a && teamBySlot === 'A') || (dataComputed.can_join_free_b && teamBySlot === 'B')))
            ) ||
            (dataComputed.is_challenge === 'Y' && dataComputed.teams.length === 1 && slotTeamSelected === -1 && !dataComputed.is_joined) ||
            (dataComputed.is_joined && !dataComputed.paid && dataComputed.match_status !== 'C')
          )"
          class="text-body-1 font-weight-bold mb-2"
          style="border-radius: 33px; text-transform: initial;"
          color="primary"
          rounded
          large
          block
          @click="onPayBooking"
        >
          <span v-if="dataComputed.is_challenge == 'Y' && ((dataComputed.can_join_free_a && teamBySlot === 'A') || (dataComputed.can_join_free_b && teamBySlot === 'B'))">{{ $t('deek_game.join') }}</span>
          <span v-else-if="dataComputed.is_challenge == 'N' && dataComputed.can_join && dataComputed.can_join_free">{{ $t('deek_game.join') }}</span>
          <span v-else-if="dataComputed.is_joined && !dataComputed.paid && dataComputed.match_status !== 'C'">{{ `${$t('game.pay_my_part')}` }}</span>
          <span v-else-if="dataComputed && dataComputed.total > 0">{{ `${$t('deek_game.join_for')} ${formatCurrency(dataComputed.total)}` }}</span>
          <span v-else>{{ $t('deek_game.join') }}</span>
        </v-btn>

        <v-btn
          v-else-if="slotTeamSelected === -1"
          class="text-body-1 font-weight-bold mb-2"
          style="border-radius: 33px; text-transform: initial;"
          color="primary"
          rounded
          large
          block
          :outlined="((dataComputed.can_join || (dataComputed.is_joined && !dataComputed.paid) && dataComputed.match_status !== 'C'))"
          @click="openUpdateShare(true)"
        >
          <span>{{ $t('game.share_invite') }}</span>
        </v-btn>

        <!-- <v-btn
          v-if="dataComputed.can_cancel"
          class="text-body-1 font-weight-bold mb-2"
          style="border-radius: 33px; text-transform: initial;"
          color="primary"
          rounded
          large
          block
          outlined
          @click="linkVideo"
        >
          <span>{{ $t('tooltip.cancel') }}</span>
        </v-btn> -->

        <!-- <v-btn
          v-if="dataComputed.can_leave && dataComputed.match_status !== 'C'"
          class="text-body-1 font-weight-bold mb-2"
          style="border-radius: 33px; text-transform: initial;"
          color="primary"
          rounded
          large
          block
          outlined
          @click="openUpdateLeave(true)"
        >
          <span>{{ $t('game.unjoin') }}</span>
        </v-btn> -->
      </v-card-actions>
    </div>

    <download-app
      :is-open.sync="isDialogDownload"
      @open-close="openUpdateDownload"
      @save="defaultDownloadAppByOS"
    />

    <share
      :is-open.sync="isDialogShare"
      :link="dataComputed.match_share_url"
      :icon-size="28"
      :sport="dataComputed.sport_name"
      :date="`${formatStdDate(dataComputed.match_date_str, 'datetime-landing', true, $i18n.locale)}`"
      :start-time="`${formatStdDate(dataComputed.start_time, 'time', true, $i18n.locale)} (${dataComputed.match_time} Mins)`"
      :location="dataComputed.facility_name ? `${dataComputed.facility_name} (${dataComputed.field_name})` : ''"
      :share-teams="true"
      :team-a="dataComputed.teams && dataComputed.teams.at(0) ? dataComputed.teams.at(0).name : '??'"
      :team-b="dataComputed.teams && dataComputed.teams.at(1) ? dataComputed.teams.at(1).name : '??'"
      :team-players-a="dataComputed.players_team_a ? dataComputed.players_team_a.map(player => player.fullname || '??') : []"
      :team-players-b="dataComputed.players_team_b ? dataComputed.players_team_b.map(player => player.fullname || '??') : []"
      @open-close="openUpdateShare"
    />

    <joined-match
      :is-open.sync="isDialogJoined"
      :title="joinedMessageFrom === 'pay-booking' ? $t('game.joined_match') : $t('game.joined_reservation')"
      :subtitle="joinedMessageFrom === 'pay-booking' ? $t('game.share_link') : $t('game.share_link_reservation')"
      :notify-new-slot="notifyNewSlot"
      :new-slot-message="$t('game.notify_new_slot')"
      @open-close="openUpdateJoined"
      @save="() => {
        openUpdateJoined(false)
        openUpdateShare(true)
        notifyNewSlot = false
      }"
    />

    <leave-match
      :is-open.sync="isDialogLeave"
      @open-close="openUpdateLeave"
      @save="onLeave"
    />

    <!-- <cancel-match
      :is-open.sync="isDialogCancel"
      @open-close="openUpdateCancel"
      @save="linkVideo"
    /> -->
  </div>
</template>
<script>
import {
  mdiCloseCircleOutline,
  mdiMessageText,
} from '@mdi/js'
import {
  ref, computed, watch, onMounted, getCurrentInstance,
} from '@vue/composition-api'
import {
  useRouter,
  openTab,
  defaultDownloadAppByOS,
  isDev,
  formatCurrency,
  formatStdDate,
  customDeepLink,
  isIOS,
} from '@core/utils'
import themeConfig from '@themeConfig'
import { useUtils } from '@core/libs/i18n'
import { avatarText } from '@core/utils/filter'
import {
  logUserActivity, getMatchGameCode, getMatchGameOpenCode, putHoldCardReservation, leaveMatchUser, joinMatchUser,
} from '@api'
import useCryptoJs from '@core/utils/useCryptoJs'
import useLogData from '@core/utils/useLogData'
import {
  showSuccess, showError,
} from '@/@core/utils/toasted'
import store from '@/store'

// import CancelMatch from '@/views/game/components/cancel-match/CancelMatch.vue'
import JoinedMatch from '@/views/game/components/joined-match/JoinedMatch.vue'
import LeaveMatch from '@/views/game/components/leave-match/LeaveMatch.vue'
import AppBarUserAvatar from '@core/layouts/components/app-bar/AppBarUserAvatar.vue'
import InfoCard from '@/views/game/components/info-card/InfoCard.vue'
import ActionCard from '@/views/game/components/action-card/ActionCard.vue'
import ResultsCard from '@/views/game/components/results-card/ResultsCard.vue'
import VideoCard from '@/views/game/components/video-card/VideoCard.vue'
import PlayerList from '@/views/game/components/player-list/PlayerList.vue'
import FacilityCard from '@/views/game/components/facility-card/FacilityCard.vue'
import DownloadApp from '@/views/reservation/components/download-app/DownloadApp.vue'
import Share from '@/views/components/share/Share.vue'

import useGame from './useGame'

export default {
  name: 'Game',
  components: {
    // CancelMatch,
    AppBarUserAvatar,
    PlayerList,
    DownloadApp,
    InfoCard,
    ActionCard,
    ResultsCard,
    VideoCard,
    FacilityCard,
    Share,
    JoinedMatch,
    LeaveMatch,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const { route, router } = useRouter()
    const { t, tInContext } = useUtils()
    const { userData } = useCryptoJs()
    const { logData, updateLogData, clearLogData } = useLogData()
    const {
      paymentMethods, mapTeams, getTeamName, getTeamShield, getTeamBySlot,
    } = useGame()

    const USER_ACT_VISIT_MATCH_LANDING_APP = 'Visit Match Landing App'
    const USER_ACT_SHARE_MATCH_LANDING_APP = 'Share Match Landing App'
    const USER_ACT_VIEW_MATCH_LANDING_APP = 'View Match Landing App'
    const ipAddress = ref({})
    const slotSelected = ref(-1)
    const slotTeamSelected = ref(-1)
    const notifyNewSlot = ref(false)
    const joinedMessageFrom = ref(null)
    const userLogged = ref(null)

    const dataLanding = ref({})
    const isIOSComputed = computed(() => isIOS())
    const dataComputed = computed(() => dataLanding.value)
    const computedDisabled = computed(() => {
      let isDisabled = false
      if (
        (dataComputed.value.is_challenge === 'Y' && (dataComputed.value.is_joined || !dataComputed.value.can_join)) // && dataComputed.value.teams.length === 2
        || (dataComputed.value.is_challenge === 'N' && (dataComputed.value.is_joined || !dataComputed.value.can_join)) // || !['N', 'F'].includes(dataComputed.value.match_status)
      ) isDisabled = true

      return isDisabled
    })
    const showComplexComputed = computed(
      () => dataComputed.value.complex_images
        && dataComputed.value.complex_images.length
        && dataComputed.value.complex_description,
    )
    const activeComputed = computed(() => dataComputed.value.match_status === 'Active')
    const basicComputed = computed(() => dataComputed.value.match_status === 'Basic')
    const cancelledComputed = computed(() => dataComputed.value.match_status === 'Cancelled' || dataComputed.value.match_status === 'C')
    const depositCardComputed = computed(() => {
      if (dataComputed.value.deposit_card && dataComputed.value.deposit_card.ending4) return dataComputed.value.deposit_card.ending4

      return ''
    })
    const matchCode = ref(null)
    const matchId = ref(null)
    const videoId = ref(null)
    const privateLanding = ref(false)
    const isDialogLeave = ref(false)
    const isDialogCancel = ref(false)
    const isDialogJoined = ref(false)
    const isDialogDownload = ref(false)
    const isDialogPay = ref(false)
    const isDialogShare = ref(false)

    // remove chanllenge
    const teamSelected = ref(null)
    const teamsComputed = computed(() => {
      if (!dataComputed.value.players_team_a && !dataComputed.value.players_team_b) return []

      const myTeams = mapTeams(
        dataComputed.value.players_team_a,
        dataComputed.value.players_team_b,
        dataComputed.value.is_challenge === 'Y' ? teamSelected.value : dataComputed.value.my_team,
        dataComputed.value.my_team,
        dataComputed.value.teams || [],
        dataComputed.value.is_challenge === 'Y',
      )

      return myTeams
    })
    const teamBySlot = computed(() => getTeamBySlot(slotSelected.value, teamsComputed.value))

    const logged = computed({
      get: () => store.state.auth.logged,
      set: value => {
        store.commit('appConfig/UPDATE_FOOTER_TYPE', value)
      },
    })

    const openUpdateShare = async val => {
      isDialogShare.value = val
    }

    const openUpdatePay = async val => {
      isDialogPay.value = val
    }

    const openUpdateLeave = async val => {
      isDialogLeave.value = val
    }

    const openUpdateCancel = async val => {
      isDialogCancel.value = val
    }

    const openUpdateJoined = async val => {
      isDialogJoined.value = val
    }

    const openUpdateDownload = async val => {
      isDialogDownload.value = val
    }

    const onLogin = async (pay = false, chooseTeam = false) => {
      logData.value.joinGame = 'NO'
      logData.value.matchCode = matchCode.value

      logData.value.slot = slotSelected.value
      logData.value.pay = pay
      logData.value.chooseTeam = chooseTeam
      logData.value.redirect = 'landing'
      logData.value.field = 'all'
      logData.value.date = 'all'
      logData.value.time = 'all'
      if (dataLanding.value.is_challenge === 'Y') logData.value.team = teamBySlot.value
      await updateLogData(logData.value)

      router.push({ name: 'auth-login-phone-email' })
    }

    const goToPayBooking = () => {
      const params = { code: matchCode.value, selected: slotSelected.value }
      if (teamSelected.value && teamSelected.value !== 'UNDEFINED' && dataComputed.value.is_challenge === 'Y') params.team = teamSelected.value
      else params.team = teamBySlot.value
      router.push({ name: 'pay-booking', params })
    }

    const onChallengeTeam = async () => {
      if (privateLanding.value) {
        const params = {
          code: dataComputed.value.reservation_code,
          matchCode: matchCode.value,
          team: 'B',
          payType: undefined,
        }

        router.push({
          name: 'choose-team',
          params,
        })
      } else {
        onLogin(false, true)
      }
    }

    const goToProfilePicture = () => {
      const params = {
        code: matchCode.value,
        selected: slotSelected.value,
        joined: true,
        joinedFrom: 'pay-booking',
      }
      if (teamSelected.value && teamSelected.value !== 'UNDEFINED' && dataComputed.value.is_challenge === 'Y') params.team = teamSelected.value
      router.push({
        name: 'profile-picture',
        params,
      })
    }

    const fetchMatch = async () => {
      let response = null
      if (userLogged.value) {
        if (teamSelected.value && teamSelected.value !== 'UNDEFINED') {
          response = await getMatchGameCode(matchCode.value, teamSelected.value)
        } else {
          response = await getMatchGameCode(matchCode.value)
        }
      } else if (teamSelected.value && teamSelected.value !== 'UNDEFINED') {
        response = await getMatchGameOpenCode(matchCode.value, teamSelected.value)
      } else {
        response = await getMatchGameOpenCode(matchCode.value)
      }
      if (response.ok) {
        dataLanding.value = response.data
        const {
          joined, joinedMessage, joinedFrom, selected, pay, chooseTeam,
        } = route.value.params

        let finded = [...dataLanding.value.players_team_a, ...dataLanding.value.players_team_b].find(e => e.selected)
        if (!finded) finded = [...dataLanding.value.players_team_a, ...dataLanding.value.players_team_b].find(e => e.additional)

        if (dataLanding.value.is_challenge === 'Y' && dataLanding.value.teams.length === 1 && !selected && teamSelected.value !== 'A') {
          slotSelected.value = -1
          slotTeamSelected.value = 0
        } else if (computedDisabled.value) {
          slotSelected.value = -1
          slotTeamSelected.value = -1
          // eslint-disable-next-line no-dupe-else-if
        } else if (dataLanding.value.is_challenge === 'Y' && dataLanding.value.teams.length === 1 && !selected && teamSelected.value !== 'A') {
          slotSelected.value = -1
          slotTeamSelected.value = 0
        } else if (selected) {
          slotSelected.value = selected
          slotTeamSelected.value = -1
        } else if (finded && !dataLanding.is_joined) {
          slotSelected.value = finded.slot
        } else {
          slotSelected.value = -1
          slotTeamSelected.value = -1
        }

        if (dataLanding.value.is_challenge === 'Y' && userLogged.value && !dataLanding.value.is_joined && chooseTeam && dataLanding.value.teams.length === 1) {
          onChallengeTeam()
        }

        if (dataLanding.value.is_challenge === 'N' && userLogged.value && pay && dataLanding.value.can_join && !dataLanding.value.can_join_free) {
          goToPayBooking()
        }

        if (dataLanding.value.is_challenge === 'N' && userLogged.value && pay && dataLanding.value.can_join && dataLanding.value.can_join_free) {
          const resp = await joinMatchUser(matchCode.value, { slot: selected })

          // eslint-disable-next-line no-use-before-define
          if (resp.ok) await initGame()
        }

        if (dataLanding.value.is_challenge === 'Y' && userLogged.value && pay && !dataLanding.value.is_joined) {
          if ((!dataLanding.value.can_join_free_a && (teamSelected.value === 'A' || teamBySlot.value === 'A')) || (!dataLanding.value.can_join_free_b && (teamSelected.value === 'B' || teamBySlot.value === 'B'))) goToPayBooking()

          if (dataLanding.value.can_join_free_a || dataLanding.value.can_join_free_b) {
            const resp = await joinMatchUser(matchCode.value, { slot: selected })

            // eslint-disable-next-line no-use-before-define
            if (resp.ok) await initGame()
          }
        }

        if (joined && dataLanding.value.is_joined) {
          notifyNewSlot.value = joinedMessage
          joinedMessageFrom.value = joinedFrom
          openUpdateJoined(true)
        }
        matchId.value = dataLanding.value.match_id
        videoId.value = dataLanding.value.video_id

        // remove chanllenge
        if (!dataLanding.value.is_joined) {
          if (dataLanding.value.is_challenge === 'Y') {
            let selectSlot = false
            if (teamSelected.value === 'A') {
              dataLanding.value.players_team_a.forEach(e => {
                if (e.empty) {
                  e.selectable = true
                  if (!selectSlot) {
                    selectSlot = true
                    e.selected = true
                  }
                } else e.selectable = false
              })
              // eslint-disable-next-line no-return-assign
              dataLanding.value.players_team_b.map(e => e.selectable = false)
            } else if (teamSelected.value === 'B') {
              dataLanding.value.players_team_b.forEach(e => {
                if (e.empty) {
                  e.selectable = true
                  if (!selectSlot) {
                    selectSlot = true
                    e.selected = true
                  }
                } else e.selectable = false
              })
              // eslint-disable-next-line no-return-assign
              dataLanding.value.players_team_a.map(e => e.selectable = false)
            } else {
              dataLanding.value.players_team_a.forEach(e => {
                if (e.empty) {
                  e.selectable = true
                  if (!selectSlot) {
                    selectSlot = true
                    e.selected = true
                  }
                } else e.selectable = false
              })
              dataLanding.value.players_team_b.forEach(e => {
                if (e.empty) {
                  e.selectable = true
                  if (!selectSlot) {
                    selectSlot = true
                    e.selected = true
                  }
                } else e.selectable = false
              })
            }
          } else {
            // eslint-disable-next-line array-callback-return
            dataLanding.value.players_team_a.map(e => {
              if (e.empty) e.selectable = true
              else e.selectable = false
            })
            // eslint-disable-next-line array-callback-return
            dataLanding.value.players_team_b.map(e => {
              if (e.empty) e.selectable = true
              else e.selectable = false
            })
          }
        }
      } else {
        router.replace({
          name: 'match-not-found',
          params: {
            inactive: false,
            invalid: true,
          },
        })
      }
    }

    const fetchLogUserActivity = async action => {
      await logUserActivity({
        ip_address: privateLanding.value ? null : ipAddress.value.ip,
        user_id: privateLanding.value ? userData.value.user_id : null,
        match_code: matchCode.value,
        reference: null,
        action,
      })
    }

    const linkVideo = (isVideo = false) => {
      customDeepLink(
        isVideo ? 'video' : 'match',
        isVideo ? videoId.value : matchId.value,
      )

      openUpdateDownload(true)
    }

    const formatTime = time => {
      const [min] = time.split('mins')

      return `${min} mins`
    }

    const onPay = async value => {
      const resp = await putHoldCardReservation(matchCode.value, value)
      if (resp.ok) {
        openUpdatePay(false)
        await fetchMatch()
      } else showError(resp.message.text)
    }

    const initGame = async () => {
      await clearLogData()

      userLogged.value = await JSON.parse(localStorage.getItem('userData'))
      privateLanding.value = !!userLogged.value

      // remove team
      const { code, team } = route.value.params

      matchCode.value = code

      // remove chanllenge
      teamSelected.value = `${team}`.toUpperCase()
      localStorage.setItem('matchCode', JSON.stringify(code))

      await fetchMatch()

      if (!userLogged.value) {
        await fetch('https://api.ipify.org/?format=json')
          .then(ipResponse => ipResponse.json())
          .then(async ipData => {
            ipAddress.value = ipData
          })
      }
    }

    const onPayBooking = async () => {
      if (privateLanding.value) {
        if (slotSelected.value > -1 || (dataComputed.value.is_joined && !dataComputed.value.paid && dataComputed.value.match_status !== 'C')) {
          if (
            (dataComputed.value.is_challenge === 'N' && dataComputed.value.can_join_free)
            || (dataComputed.value.is_challenge === 'Y'
            && (!dataComputed.value.can_join_free && ((dataComputed.value.can_join_free_a && teamBySlot.value === 'A') || (dataComputed.value.can_join_free_b && teamBySlot.value === 'B')))
            )
          ) {
            const resp = await joinMatchUser(matchCode.value, { slot: slotSelected.value })
            if (resp.ok) {
              if (userData.value.avatar === null || userData.value.avatar.includes('no-profile-pic')) {
                goToProfilePicture()
              } else await initGame()
            }
          } else {
            goToPayBooking()
          }
        }
      } else {
        onLogin(true)
      }
    }

    const onLeave = async () => {
      const resp = await leaveMatchUser(matchCode.value)
      const msg = tInContext('game.leaved_match', vm)
      if (resp.ok) {
        await initGame()
        openUpdateLeave(false)
        showSuccess(msg)
      } else {
        showError(resp.message.text)
      }
    }

    const onLogout = async () => {
      await localStorage.removeItem('userData')
      paymentMethods.value = []

      await initGame()
    }

    watch([logged], async () => {
      await initGame()
    })

    onMounted(async () => {
      await initGame()
    })

    return {
      USER_ACT_VISIT_MATCH_LANDING_APP,
      USER_ACT_SHARE_MATCH_LANDING_APP,
      USER_ACT_VIEW_MATCH_LANDING_APP,
      ipAddress,
      userData,
      isDev,
      paymentMethods,
      matchId,
      videoId,
      slotSelected,
      slotTeamSelected,

      // remove chanllenge
      teamSelected,
      notifyNewSlot,
      joinedMessageFrom,
      userLogged,
      teamsComputed,
      teamBySlot,

      matchCode,
      privateLanding,
      isDialogLeave,
      isDialogCancel,
      isDialogJoined,
      isDialogDownload,
      isDialogPay,
      isDialogShare,

      dataComputed,
      computedDisabled,
      depositCardComputed,
      showComplexComputed,
      activeComputed,
      basicComputed,
      cancelledComputed,
      isIOSComputed,

      appLogoTitle: themeConfig.app.logoTitleBlack,

      avatarText,
      openTab,
      onLogin,
      linkVideo,
      customDeepLink,
      fetchMatch,
      openUpdateDownload,
      openUpdatePay,
      openUpdateShare,
      openUpdateLeave,
      openUpdateCancel,
      openUpdateJoined,
      fetchLogUserActivity,
      onLogout,
      onPayBooking,
      onPay,
      defaultDownloadAppByOS,
      formatTime,
      formatCurrency,
      mapTeams,
      getTeamName,
      getTeamShield,
      formatStdDate,
      onLeave,
      onChallengeTeam,

      t,

      icons: {
        mdiCloseCircleOutline,
        mdiMessageText,
        videoWhite: require('@/assets/images/svg/video-white.svg'),
        videoGray: require('@/assets/images/svg/video-gray.svg'),
        ranking: require('@/assets/images/svg/ranking.svg'),
        smileyHappy: require('@/assets/images/svg/smiley-happy.svg'),
        frsWhite: require('@/assets/images/icons/frs-white.png'),
      },
    }
  },
}
</script>

<style lang="scss" scoped>
// @import '@core/preset/preset/pages/landing.scss';
@import '@core/preset/preset/pages/game.scss';

.theme--dark.v-divider {
  border-color: #333333;
}

.bg-grey {
  background-color: #979797;
}
</style>
